import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../contexts/AlertContext';

import UserList from '../components/admin/UserList';
import DashboardStats from '../components/admin/DashboardStats';
import SubscriptionManager from '../components/admin/SubscriptionManager';
import ClaimsTierManager from '../components/admin/ClaimsTierManager';
import PasswordResetManager from '../components/admin/PasswordResetManager';
import TierSettings from '../components/admin/TierSettings';
import AIPromptsManager from '../components/admin/AIPromptsManager';
import Navigation from '../components/Navigation';
import { getAccessToken } from '../utils/auth';

type TabType = 'overview' | 'users' | 'subscriptions' | 'claims' | 'passwords' | 'tiers' | 'ai-prompts';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const { alert } = useAlert();

  useEffect(() => {
    const checkAdminStatus = async () => {
      const token = getAccessToken();
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (!payload.admin) {
          await alert({
            title: 'Access Denied',
            message: 'Admin privileges required to access this page.',
            variant: 'error',
          });
          navigate('/uploads');
          return;
        }
        setIsAdmin(true);
      } catch (error) {
        console.error('Error checking admin status:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [navigate, alert]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0b0f]">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-zinc-500">Loading admin dashboard...</div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const tabs: { key: TabType; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'users', label: 'Users' },
    { key: 'subscriptions', label: 'Subscriptions' },
    { key: 'claims', label: 'Claims Tiers' },
    { key: 'tiers', label: 'Tier Settings' },
    { key: 'passwords', label: 'Password Reset' },
    { key: 'ai-prompts', label: 'AI Prompts' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0b0f]">
      <Navigation />

      {/* Header */}
      <div className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-2xl font-semibold text-zinc-100">Admin Dashboard</h1>
            <p className="text-zinc-500 mt-1 text-sm">Manage users, subscriptions, and system settings</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`tab-dark whitespace-nowrap ${
                  activeTab === tab.key ? 'active' : ''
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card-dark rounded-xl p-6">
          {activeTab === 'overview' && <DashboardStats />}
          {activeTab === 'users' && <UserList />}
          {activeTab === 'subscriptions' && <SubscriptionManager />}
          {activeTab === 'claims' && <ClaimsTierManager />}
          {activeTab === 'tiers' && <TierSettings />}
          {activeTab === 'passwords' && <PasswordResetManager />}
          {activeTab === 'ai-prompts' && <AIPromptsManager />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
